import type { AuthClient } from './AuthClient';
import { type CognitoEnvironment, configureAmplify } from './amplifyConfig';
import { CognitoAuthClient } from './CognitoAuthClient';
import { SimulatedAuthClient } from './SimulatedAuthClient';

export type AuthEnvironment = CognitoEnvironment & {
    VITE_AUTH_PROVIDER?: string;
    /** Vite's build time constant: true for the dev server and for a build in development mode. */
    DEV: boolean;
};

/**
 * The single place that decides which authentication provider the application runs against.
 *
 * Adding a provider means adding one `AuthClient` implementation and one branch here, selected
 * through `VITE_AUTH_PROVIDER`. No other module imports a concrete client.
 *
 * The environment is a parameter so tests can select a provider without rebuilding.
 */
export const createAuthClient = (environment: AuthEnvironment = import.meta.env): AuthClient => {
    const provider = environment.VITE_AUTH_PROVIDER ?? 'simulated';

    switch (provider) {
        case 'simulated':
            /*
             * The simulated client accepts a well known email and password, which is acceptable only
             * while it cannot run anywhere but a developer machine. Hence two checks that look
             * redundant and are not:
             *
             * `import.meta.env.DEV` is a build time literal. In a staging, preprod or production
             * bundle it makes this throw unconditional, so the branch below and the simulated client
             * module with it are dropped from the output rather than merely left unreachable. That is
             * what keeps the credentials out of a deployed bundle entirely.
             *
             * `environment.DEV` is the injected value, and is what lets a test assert this refusal:
             * under the test runner the literal is always true.
             */
            if (!import.meta.env.DEV || !environment.DEV) {
                throw new Error(
                    'The simulated authentication provider is only available in a development build. ' +
                        'Set VITE_AUTH_PROVIDER=cognito for this environment.'
                );
            }

            return new SimulatedAuthClient(window.localStorage);
        case 'cognito':
            // Amplify has to be configured before the client calls any of its functions.
            configureAmplify(environment);

            return new CognitoAuthClient();
        default:
            throw new Error(`Unsupported authentication provider: ${provider}`);
    }
};
